/**
 * A small allowlist sanitiser for everything that reaches `dangerouslySetInnerHTML`.
 *
 * `textBloc`, `mainTitle`, `subtTitle` and `topSpacesWidget`'s header and column cells render
 * strings as raw HTML (legacy `v-html`). Config JSON is operator-authored and trusted, but
 * `topSpacesWidget` renders **broker-sourced** strings that way, which makes anything able to
 * publish to that topic able to inject markup into a wall-mounted display.
 *
 * The decision (docs/MIGRATION-WIDGETS.md §4) is to sanitise every path rather than to
 * classify them, because a widget's text can be part config and part payload after
 * `{{variable}}` substitution and the two are indistinguishable by then. `titleBloc` keeps
 * its escaped (`v-text`) rendering and does not come through here at all.
 *
 * No dependency: the allowlist is eight inline tags and one attribute, and a sanitiser small
 * enough to read in full is easier to trust here than a transitive dependency tree on a
 * device nobody can patch quickly.
 */

/** Formatting the deployed configs use, plus the obvious inline siblings. */
const ALLOWED_TAGS = new Set(['B', 'I', 'EM', 'STRONG', 'BR', 'SPAN', 'SUP', 'SUB']);

/** No `class` (Tailwind is not in the config's vocabulary) and no `id`, `href` or handlers. */
const ALLOWED_ATTRIBUTES = new Set(['style']);

/** HTML with no closing tag. */
const VOID_TAGS = new Set(['BR']);

/**
 * Elements whose *contents* are dropped with them.
 *
 * Everything else is unwrapped — a `<div>` becomes its own text — so that disallowed markup
 * costs formatting and never the message. That is wrong for these, whose text content is code
 * rather than prose: unwrapping a `<script>` would print the script on the wall.
 */
const OPAQUE_TAGS = new Set([
  'SCRIPT',
  'STYLE',
  'IFRAME',
  'OBJECT',
  'EMBED',
  'NOSCRIPT',
  'TEMPLATE',
  'SVG',
  'MATH',
]);

/**
 * Returns `html` with every tag outside the allowlist unwrapped or dropped, every attribute
 * outside the allowlist removed, and all text escaped.
 *
 * Parsing is done with `DOMParser` rather than a regex: `text/html` parsing is inert — no
 * script runs, no resource is fetched — and it is the only way to get the same tolerance for
 * malformed markup that the browser would otherwise apply to the string we are guarding.
 */
export function sanitizeHtml(html: string): string {
  // The overwhelmingly common case is a plain sentence with no markup at all. Parsing it
  // would allocate a document per widget per MQTT message for no change in the output.
  if (!html.includes('<') && !html.includes('&')) return html;

  const document = new DOMParser().parseFromString(html, 'text/html');
  return serialiseChildren(document.body);
}

function serialiseChildren(node: Node): string {
  let html = '';
  for (const child of node.childNodes) html += serialise(child);
  return html;
}

function serialise(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return escapeText(node.nodeValue ?? '');
  if (node.nodeType !== Node.ELEMENT_NODE) return ''; // comments, CDATA, processing instructions

  const element = node as Element;
  const tag = element.tagName.toUpperCase();

  if (OPAQUE_TAGS.has(tag)) return '';
  if (!ALLOWED_TAGS.has(tag)) return serialiseChildren(element);

  const name = tag.toLowerCase();
  const attributes = serialiseAttributes(element);
  if (VOID_TAGS.has(tag)) return `<${name}${attributes}>`;

  return `<${name}${attributes}>${serialiseChildren(element)}</${name}>`;
}

function serialiseAttributes(element: Element): string {
  let attributes = '';
  for (const { name, value } of element.attributes) {
    if (!ALLOWED_ATTRIBUTES.has(name.toLowerCase())) continue;
    attributes += ` ${name.toLowerCase()}="${escapeAttribute(value)}"`;
  }
  return attributes;
}

function escapeText(text: string): string {
  return text.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');
}

function escapeAttribute(value: string): string {
  return escapeText(value).replaceAll('"', '&quot;');
}
